import { useRef, useCallback, useEffect } from 'react';

// ⚡️ PERFORMANCE OPTIMIZATION: Web Worker hook for CPU-heavy tasks
export const useWebWorker = () => {
  const workerRef = useRef(null);
  const pendingRequests = useRef(new Map());

  // ⚡️ PERFORMANCE OPTIMIZATION: Initialize worker
  useEffect(() => {
    if (typeof Worker !== 'undefined') {
      try {
        workerRef.current = new Worker('/worker.js');
        
        // ⚡️ ERROR HANDLING: Add error listener for importScripts failures
        workerRef.current.addEventListener('error', (event) => {
          if (event.error && event.error.message && event.error.message.includes('importScripts')) {
            console.warn('Web Worker library loading failed, using fallback methods');
          }
        });
        
        // ⚡️ PERFORMANCE OPTIMIZATION: Handle worker messages
        workerRef.current.onmessage = (event) => {
          const { id, success, result, error } = event.data;
          
          if (pendingRequests.current.has(id)) {
            const { resolve, reject } = pendingRequests.current.get(id);
            pendingRequests.current.delete(id);
            
            if (success) {
              resolve(result);
            } else {
              reject(new Error(error));
            }
          }
        };
        
        // ⚡️ PERFORMANCE OPTIMIZATION: Handle worker errors
        workerRef.current.onerror = (error) => {
          console.warn('Web Worker error (falling back to main thread):', error);
          // Clear all pending requests on error and fallback to main thread
          pendingRequests.current.forEach(({ resolve, reject }) => {
            try {
              const result = handleInMainThread('SANITIZE_TEXT', { text: '' });
              resolve(result);
            } catch (err) {
              reject(new Error('Worker error - fallback failed'));
            }
          });
          pendingRequests.current.clear();
        };
        
        console.log('Web Worker initialized successfully');
      } catch (error) {
        console.error('Failed to initialize Web Worker:', error);
      }
    }
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Cleanup worker on unmount
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
        pendingRequests.current.clear();
      }
    };
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Send message to worker
  const sendMessage = useCallback((type, data) => {
    return new Promise((resolve, reject) => {
      if (!workerRef.current) {
        // Fallback to main thread if worker is not available
        resolve(handleInMainThread(type, data));
        return;
      }
      
      const id = Date.now() + Math.random();
      pendingRequests.current.set(id, { resolve, reject });
      
      workerRef.current.postMessage({
        type,
        data,
        id
      });
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Timeout for worker requests
      setTimeout(() => {
        if (pendingRequests.current.has(id)) {
          pendingRequests.current.delete(id);
          reject(new Error('Worker request timeout'));
        }
      }, 10000); // 10 second timeout
    });
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Fallback functions for main thread
  const handleInMainThread = (type, data) => {
    switch (type) {
      case 'SANITIZE_TEXT':
        return sanitizeTextInMainThread(data.text);
      case 'ENCRYPT_TEXT':
        return encryptTextInMainThread(data.text, data.key);
      case 'DECRYPT_TEXT':
        return decryptTextInMainThread(data.text, data.key);
      case 'PROCESS_MESSAGES':
        return processMessagesInMainThread(data.messages, data.filters);
      case 'VALIDATE_FILE':
        return validateFileInMainThread(data.file);
      default:
        throw new Error(`Unknown message type: ${type}`);
    }
  };

  // ⚡️ PERFORMANCE OPTIMIZATION: Main thread fallback functions
  const sanitizeTextInMainThread = (text) => {
    // Simple text sanitization for main thread fallback
    if (!text || typeof text !== 'string') return '';
    return text.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  };

  const encryptTextInMainThread = (text, key) => {
    // Simple encryption fallback
    if (!text || !key) return '';
    try {
      // Use a simple XOR encryption as fallback
      let result = '';
      for (let i = 0; i < text.length; i++) {
        result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return btoa(result);
    } catch (error) {
      return text;
    }
  };

  const decryptTextInMainThread = (encryptedText, key) => {
    // Simple decryption fallback
    if (!encryptedText || !key) return '';
    try {
      const decoded = atob(encryptedText);
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return result;
    } catch (error) {
      return encryptedText;
    }
  };

  const processMessagesInMainThread = (messages, filters = {}) => {
    if (!Array.isArray(messages)) return [];
    
    let processedMessages = messages;
    
    if (filters.textFilter) {
      processedMessages = processedMessages.filter(msg => 
        msg.text && msg.text.toLowerCase().includes(filters.textFilter.toLowerCase())
      );
    }
    
    if (filters.typeFilter) {
      processedMessages = processedMessages.filter(msg => 
        msg.type === filters.typeFilter
      );
    }
    
    processedMessages.sort((a, b) => {
      const aTime = a.timestamp?.seconds || a.createdAt || 0;
      const bTime = b.timestamp?.seconds || b.createdAt || 0;
      return aTime - bTime;
    });
    
    return processedMessages;
  };

  const validateFileInMainThread = (fileData) => {
    const { name, size, type } = fileData;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (size > maxSize) {
      return {
        valid: false,
        error: 'File size exceeds 10MB limit'
      };
    }
    
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/webm', 'video/ogg',
      'application/pdf', 'text/plain'
    ];
    
    if (!allowedTypes.includes(type)) {
      return {
        valid: false,
        error: 'File type not allowed'
      };
    }
    
    return {
      valid: true,
      file: fileData
    };
  };

  // ⚡️ PERFORMANCE OPTIMIZATION: Convenience methods
  const sanitizeText = useCallback((text) => {
    return sendMessage('SANITIZE_TEXT', { text });
  }, [sendMessage]);

  const encryptText = useCallback((text, key) => {
    return sendMessage('ENCRYPT_TEXT', { text, key });
  }, [sendMessage]);

  const decryptText = useCallback((text, key) => {
    return sendMessage('DECRYPT_TEXT', { text, key });
  }, [sendMessage]);

  const processMessages = useCallback((messages, filters) => {
    return sendMessage('PROCESS_MESSAGES', { messages, filters });
  }, [sendMessage]);

  const validateFile = useCallback((file) => {
    return sendMessage('VALIDATE_FILE', { file });
  }, [sendMessage]);

  return {
    sanitizeText,
    encryptText,
    decryptText,
    processMessages,
    validateFile,
    isWorkerAvailable: !!workerRef.current
  };
}; 